import { type Qualification } from "@clipboard-health/contract-worker-app-bff";
import { useMemo } from "react";

import { useGetQualifications } from "../Qualifications/api/useGetQualifications";
import { useDefinedWorker } from "./useDefinedWorker";

export const WORKER_QUALIFICATIONS_QUERY_KEY = "use-worker-qualifications";

/**
 * Returns the qualifications for the current worker based on their shoppable qualifications.
 * The qualifications are sorted by name.
 */
export function useWorkerQualifications(options?: {
  // Allow caller to avoid redundantly refetching on window focus
  refetchOnWindowFocus?: boolean;
}) {
  const { shoppableQualifications } = useDefinedWorker();

  const {
    data: qualificationsData,
    isLoading: qualificationsAreLoading,
    isFetching: qualificationsAreFetching,
    isError: qualificationsAreError,
    error: qualificationsError,
    isSuccess: qualificationsAreLoaded,
    refetch: refetchQualifications,
    status,
  } = useGetQualifications(
    // this is an api filter, not react-query enabled
    { enabled: true },
    {
      ...(options?.refetchOnWindowFocus && { refetchOnWindowFocus: options.refetchOnWindowFocus }),
    }
  );

  const workerQualifications = useMemo(() => {
    if (qualificationsAreError || !qualificationsData) {
      return [];
    }

    const qualificationIds = new Set(
      shoppableQualifications.map(
        (shoppableQualification) => shoppableQualification.relationships.qualification.data.id
      )
    );

    const qualifications: Qualification[] =
      qualificationsData.data.filter((qualification) => qualificationIds.has(qualification.id)) ??
      [];

    qualifications.sort((a, b) => a.attributes.name.localeCompare(b.attributes.name));
    return qualifications;
  }, [shoppableQualifications, qualificationsData, qualificationsAreError]);

  return {
    data: workerQualifications,
    isLoading: qualificationsAreLoading,
    isFetching: qualificationsAreFetching,
    isError: qualificationsAreError,
    error: qualificationsError,
    isSuccess: qualificationsAreLoaded,
    refetch: refetchQualifications,
    status,
  };
}
